/**
 * Noir productconfigurator.
 *
 * Bron van waarheid = Shopify-varianten (prijs, voorraad, bestelling). Deze code:
 *  - zoekt bij elke keuze de bijbehorende variant op en zet het id in het winkelwagenformulier,
 *  - toont prijs/beschikbaarheid uit die variant,
 *  - kleurt het embleem in de productfoto met vooraf voorbereide lagen (geen AI, deterministisch).
 *
 * Herkleuring (identiek aan de gecontroleerde simulatie):
 *   embleem:        pixel = T * s            (s <= 1)
 *                   pixel = T + (255 - T) * min(1, (s - 1) * HIGHLIGHT)   (s > 1)
 *   spiegelbeeld:   pixel = O + som( a * s * (T_nieuw - T_orig) )
 */
(function () {
  'use strict';

  // De onderdelen en hun kanaal (R,G,B in de gepakte lagen) komen uit de renderconfiguratie van het product.

  function hexToRgb(hex) {
    var h = String(hex || '#000000').replace('#', '');
    if (h.length === 3) h = h.replace(/(.)/g, '$1$1');
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }

  function loadPixels(url, w, h) {
    return fetch(url, { mode: 'cors', credentials: 'omit', headers: { Accept: 'image/png,image/*;q=0.8' } })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url);
        return r.blob();
      })
      .then(function (b) {
        return createImageBitmap(b, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
      })
      .then(function (bmp) {
        var c = document.createElement('canvas');
        c.width = bmp.width;
        c.height = bmp.height;
        var ctx = c.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(bmp, 0, 0);
        var d = ctx.getImageData(0, 0, c.width, c.height);
        if (d.width !== w || d.height !== h) throw new Error('Onverwacht formaat voor ' + url + ': ' + d.width + 'x' + d.height);
        return d;
      });
  }

  function Configurator(root) {
    this.root = root;
    this.data = JSON.parse(root.querySelector('[data-noir-pc-data]').textContent);
    this.groups = Array.prototype.slice.call(root.querySelectorAll('[data-pc-group]'));
    this.priceEl = root.querySelector('[data-pc-price]');
    this.stockEl = root.querySelector('[data-pc-stock]');
    this.skuEl = root.querySelector('[data-pc-sku]');
    this.idInput = root.querySelector('[data-pc-variant-id]');
    this.submit = root.querySelector('[data-pc-submit]');
    this.submitLabel = root.querySelector('[data-pc-submit-label]');
    this.unavailEl = root.querySelector('[data-pc-unavail]');
    this.baseImg = root.querySelector('[data-pc-base]');
    this.stage = root.querySelector('[data-pc-stage]');
    this.emblemCanvas = root.querySelector('[data-pc-layer="emblem"]');
    this.reflCanvas = root.querySelector('[data-pc-layer="reflection"]');
    this.errorEl = root.querySelector('[data-pc-preview-error]');
    this.noteEl = root.querySelector('[data-pc-note]');
    this.surRow = root.querySelector('[data-pc-surcharge-row]');
    this.surVal = root.querySelector('[data-pc-surcharge]');
    this.surNote = root.querySelector('[data-pc-surnote]');
    this.surNoteAmount = root.querySelector('[data-pc-surnote-amount]');
    this.stdButtons = Array.prototype.slice.call(root.querySelectorAll('[data-pc-std]'));

    this.variantMap = {};
    this.data.variants.forEach(function (v) { this.variantMap[v.options.join('|')] = v; }, this);
    this.sel = new Array(this.data.options.length);
    this.groups.forEach(function (g) {
      var checked = g.querySelector('input:checked');
      if (checked) this.sel[parseInt(g.dataset.optionIndex, 10)] = checked.value;
    }, this);

    this.layers = null; // pixelgegevens als geladen
    this.layersFailed = false;
    this.watchHeader();
    this.bind();
    this.refresh(true);
    this.loadLayers();
  }

  Configurator.prototype.bind = function () {
    var self = this;
    this.groups.forEach(function (g) {
      g.addEventListener('change', function (e) {
        var input = e.target;
        if (!input || input.type !== 'radio') return;
        self.sel[parseInt(g.dataset.optionIndex, 10)] = input.value;
        self.refresh(false);
      });
    });
    this.stdButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { self.resetToStandard(); });
    });
    if ('ResizeObserver' in window && this.stage) {
      new ResizeObserver(function () { self.paint(); }).observe(this.stage);
    }
  };

  // Zet alle keuzes terug op het standaardontwerp (de variant met de standaardprijs).
  Configurator.prototype.resetToStandard = function () {
    var std = this.data.standard;
    if (!std || !std.options) return;
    this.sel = std.options.slice();
    this.groups.forEach(function (g) {
      var idx = parseInt(g.dataset.optionIndex, 10);
      g.querySelectorAll('input[type="radio"]').forEach(function (input) {
        input.checked = input.value === std.options[idx];
      });
    });
    this.refresh(false);
  };

  // Houdt de vastgezette preview onder de (vastgezette) header, ook als die verdwijnt/verschijnt.
  Configurator.prototype.watchHeader = function () {
    var header = document.querySelector('.section-header') || document.getElementById('shopify-section-header');
    var root = this.root;
    if (!header) return;
    function update() {
      var sticky = header.classList.contains('shopify-section-header-sticky');
      var hidden = header.classList.contains('shopify-section-header-hidden');
      var h = sticky && !hidden ? Math.round(header.getBoundingClientRect().height) : 0;
      root.style.setProperty('--noir-sticky-top', h + 'px');
    }
    update();
    if ('MutationObserver' in window) new MutationObserver(update).observe(header, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', update, { passive: true });
  };

  Configurator.prototype.findVariant = function (sel) {
    return this.variantMap[sel.join('|')] || null;
  };

  Configurator.prototype.refresh = function (initial) {
    var self = this;
    var variant = this.findVariant(this.sel);
    this.variant = variant;

    // beschikbaarheid per optie: bestaat er een variant als je alleen deze groep wijzigt?
    this.groups.forEach(function (g) {
      var idx = parseInt(g.dataset.optionIndex, 10);
      g.querySelectorAll('input[type="radio"]').forEach(function (input) {
        if (input.hasAttribute('data-soon')) return; // statisch niet kiesbaar
        var cand = self.sel.slice();
        cand[idx] = input.value;
        var exists = !!self.findVariant(cand);
        input.disabled = !exists && !input.checked;
        input.closest('[data-pc-swatch]').classList.toggle('is-unavailable', !exists);
      });
      var nameEl = g.querySelector('[data-pc-selected-name]');
      if (nameEl) nameEl.textContent = self.sel[idx] || '';
    });

    // samenvatting
    this.data.zones.forEach(function (z, i) {
      var el = self.root.querySelector('[data-pc-sum="' + i + '"]');
      var g = self.groups.filter(function (x) { return x.dataset.kind === 'zone' && x.dataset.zone === String(i); })[0];
      if (el && g) el.textContent = self.sel[parseInt(g.dataset.optionIndex, 10)] || '';
    });
    var finGroup = this.groups.filter(function (x) { return x.dataset.kind === 'finish'; })[0];
    var finEl = this.root.querySelector('[data-pc-sum="finish"]');
    if (finEl && finGroup) finEl.textContent = this.sel[parseInt(finGroup.dataset.optionIndex, 10)] || '';

    // prijs, voorraad, winkelwagenknop, variant-id
    if (variant) {
      this.priceEl.textContent = variant.price;
      this.skuEl.textContent = variant.sku || '';
      this.stockEl.hidden = variant.available;
      this.idInput.value = variant.id;
      this.idInput.disabled = !variant.available;
      this.submit.disabled = !variant.available;
      this.submit.removeAttribute('aria-disabled');
      this.submitLabel.textContent = variant.available ? this.data.i18n.add : this.data.i18n.soldOut;
      this.unavailEl.hidden = true;
      // toeslag voor een eigen kleurcombinatie (verschil t.o.v. het standaardontwerp, uit de varianten zelf)
      var sur = variant.surchargeCents > 0;
      if (this.surRow) this.surRow.hidden = !sur;
      if (this.surNote) this.surNote.hidden = !sur;
      if (sur) {
        if (this.surVal) this.surVal.textContent = variant.surcharge;
        if (this.surNoteAmount) this.surNoteAmount.textContent = variant.surcharge;
      }
    } else {
      // veilig: geen geldige configuratie = niets te bestellen
      this.idInput.disabled = true;
      this.submit.disabled = true;
      this.submitLabel.textContent = this.data.i18n.unavailable;
      this.unavailEl.hidden = false;
    }

    // dynamische checkoutknoppen (Shop Pay e.d.) volgen het variant-id
    if (!initial) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));

    // deelbare link
    if (variant && window.history && history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(null, '', url.toString());
    }

    // knop "standaardplaat": actief tonen als het standaardontwerp al gekozen is
    var isStd = !!(variant && this.data.standard && variant.id === this.data.standard.id);
    this.stdButtons.forEach(function (btn) {
      var label = btn.querySelector('[data-pc-std-label]');
      btn.setAttribute('aria-pressed', isStd ? 'true' : 'false');
      btn.classList.toggle('is-active', isStd);
      if (label && self.data.standard) {
        label.textContent = (isStd ? 'Standaardplaat gekozen · ' : 'Terug naar standaardplaat · ') + self.data.standard.price;
      }
    });

    this.updateBase(finGroup);
    this.updateAlt();
    this.paint();
  };

  Configurator.prototype.updateBase = function (finGroup) {
    if (!finGroup) return;
    var name = this.sel[parseInt(finGroup.dataset.optionIndex, 10)];
    var fin = this.data.finishes.filter(function (f) { return f.name === name; })[0];
    if (fin && fin.base && fin.base.src && this.baseImg.getAttribute('src') !== fin.base.src) {
      this.baseImg.setAttribute('src', fin.base.src);
      this.baseImg.setAttribute('srcset', fin.base.srcset);
    }
  };

  Configurator.prototype.updateAlt = function () {
    var parts = [];
    this.data.zones.forEach(function (z, i) {
      var g = this.groups.filter(function (x) { return x.dataset.kind === 'zone' && x.dataset.zone === String(i); })[0];
      if (g) parts.push(z.label.toLowerCase() + ': ' + (this.sel[parseInt(g.dataset.optionIndex, 10)] || ''));
    }, this);
    var finGroup = this.groups.filter(function (x) { return x.dataset.kind === 'finish'; })[0];
    if (finGroup) parts.push(this.sel[parseInt(finGroup.dataset.optionIndex, 10)] || '');
    this.baseImg.alt = this.data.productTitle + ' - ' + parts.join(', ');
  };

  /* ---------------- Preview-lagen ---------------- */

  Configurator.prototype.loadLayers = function () {
    var self = this;
    var rc = this.data.render, a = this.data.assets;
    var E = rc.emblem, R = rc.reflection || { w: 1, h: 1 };
    Promise.all([
      loadPixels(a.emblemAlpha, E.w, E.h),
      loadPixels(a.emblemShade, E.w, E.h),
      rc.reflection && a.reflAlpha ? loadPixels(a.reflAlpha, R.w, R.h) : null,
      rc.reflection && a.reflShade ? loadPixels(a.reflShade, R.w, R.h) : null,
      rc.reflection && a.reflBase ? loadPixels(a.reflBase, R.w, R.h) : null
    ]).then(function (r) {
      self.layers = { eA: r[0], eS: r[1], rA: r[2], rS: r[3], rB: r[4] };
      self.paint();
    }).catch(function (err) {
      self.layersFailed = true;
      if (window.console) console.warn('[noir-configurator] preview-lagen niet geladen:', err);
      self.emblemCanvas.hidden = true;
      if (self.reflCanvas) self.reflCanvas.hidden = true;
      self.errorEl.hidden = false;
      self.noteEl.hidden = true;
    });
  };

  Configurator.prototype.partColors = function () {
    var out = {};
    this.data.zones.forEach(function (z, zi) {
      var g = this.groups.filter(function (x) { return x.dataset.kind === 'zone' && x.dataset.zone === String(zi); })[0];
      var name = g ? this.sel[parseInt(g.dataset.optionIndex, 10)] : null;
      var color = z.colors.filter(function (c) { return c.name === name; })[0];
      var rgb = color ? hexToRgb(color.preview) : null;
      z.parts.forEach(function (p) { out[p] = rgb; });
    }, this);
    return out;
  };

  Configurator.prototype.paint = function () {
    if (!this.layers || this.layersFailed) return;
    var colors = this.partColors();
    var rc = this.data.render, L = this.layers, hl = this.data.highlight || rc.highlight || 0.55;
    var self = this;
    var partNames = Object.keys(rc.parts);

    // ---- embleem ----
    var E = rc.emblem, eN = E.w * E.h;
    var eOut = new ImageData(E.w, E.h);
    for (var i = 0; i < eN; i++) {
      var r = 0, g = 0, b = 0, a = 0;
      for (var pi = 0; pi < partNames.length; pi++) {
        var T = colors[partNames[pi]];
        if (!T) continue;
        var ch = rc.parts[partNames[pi]].channel;
        var pa = L.eA.data[i * 4 + ch] / 255;
        if (pa <= 0) continue;
        var s = L.eS.data[i * 4 + ch] / 128, c0, c1, c2;
        if (s <= 1) { c0 = T[0] * s; c1 = T[1] * s; c2 = T[2] * s; }
        else { var k = Math.min(1, (s - 1) * hl); c0 = T[0] + (255 - T[0]) * k; c1 = T[1] + (255 - T[1]) * k; c2 = T[2] + (255 - T[2]) * k; }
        // 'over'-compositing, rechte alpha
        var na = pa + a * (1 - pa);
        r = (c0 * pa + r * a * (1 - pa)) / na;
        g = (c1 * pa + g * a * (1 - pa)) / na;
        b = (c2 * pa + b * a * (1 - pa)) / na;
        a = na;
      }
      eOut.data[i * 4] = Math.round(r);
      eOut.data[i * 4 + 1] = Math.round(g);
      eOut.data[i * 4 + 2] = Math.round(b);
      eOut.data[i * 4 + 3] = Math.round(a * 255);
    }
    this.drawScaled(this.emblemCanvas, eOut);

    // ---- spiegelbeeld ----
    if (rc.reflection && this.reflCanvas && L.rA && L.rS && L.rB) {
      var Rr = rc.reflection, rN = Rr.w * Rr.h;
      var rOut = new ImageData(Rr.w, Rr.h);
      for (var j = 0; j < rN; j++) {
        var o0 = L.rB.data[j * 4], o1 = L.rB.data[j * 4 + 1], o2 = L.rB.data[j * 4 + 2];
        var v0 = o0, v1 = o1, v2 = o2;
        for (var q = 0; q < partNames.length; q++) {
          var Tn = colors[partNames[q]];
          if (!Tn) continue;
          var orig = rc.parts[partNames[q]].orig;
          var qc = rc.parts[partNames[q]].channel;
          var aa = L.rA.data[j * 4 + qc] / 255;
          if (aa <= 0) continue;
          var ss = L.rS.data[j * 4 + qc] / 128;
          v0 += aa * ss * (Tn[0] - orig[0]);
          v1 += aa * ss * (Tn[1] - orig[1]);
          v2 += aa * ss * (Tn[2] - orig[2]);
        }
        v0 = Math.max(0, Math.min(255, v0)); v1 = Math.max(0, Math.min(255, v1)); v2 = Math.max(0, Math.min(255, v2));
        var d = Math.max(Math.abs(v0 - o0), Math.abs(v1 - o1), Math.abs(v2 - o2));
        rOut.data[j * 4] = Math.round(v0);
        rOut.data[j * 4 + 1] = Math.round(v1);
        rOut.data[j * 4 + 2] = Math.round(v2);
        rOut.data[j * 4 + 3] = Math.round(Math.min(1, d / 4) * 255); // alleen waar de kleur echt verandert -> naadloos
      }
      this.drawScaled(this.reflCanvas, rOut);
    }
    self.stage.classList.add('is-ready');
  };

  // Tekent een klein beeld op weergaveresolutie (scherper dan CSS-schalen).
  Configurator.prototype.drawScaled = function (canvas, imageData) {
    var cssW = canvas.clientWidth, cssH = canvas.clientHeight;
    if (!cssW || !cssH) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 3);
    var w = Math.max(1, Math.round(cssW * dpr)), h = Math.max(1, Math.round(cssH * dpr));
    var off = this._off || (this._off = document.createElement('canvas'));
    off.width = imageData.width; off.height = imageData.height;
    off.getContext('2d').putImageData(imageData, 0, 0);
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    var ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(off, 0, 0, w, h);
  };

  function init(scope) {
    (scope || document).querySelectorAll('[data-noir-pc]').forEach(function (root) {
      if (root.dataset.pcInit || !root.querySelector('[data-noir-pc-data]')) return;
      root.dataset.pcInit = '1';
      root.noirConfigurator = new Configurator(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
